/**
 * PBHOrdersSQLOnly → Exchanges list display mapping (DE-9312).
 *
 * The SQL-only OData entity returns workflow fields (`RawStatus`,
 * `RawInboundDiscriminator`, `RawOutboundDiscriminator`), not the legacy
 * `InboundStatus` / `OutboundStatus` strings the UI was built around.
 *
 * Role in the migration:
 * 1. **List rows** — `mapSqlOnlyOrders` runs after every SQL-only fetch in
 *    `api.service.js` so tables, cards, and detail panels keep using
 *    `InboundStatus` / `OutboundStatus`.
 * 2. **Status filters** — display labels produced here must match values in
 *    `sqlOnlyFilterUtils.ts` (user picks a label → OData Raw* filter).
 * 3. **Action buckets** — `setActionStatusSqlOnly` compares mapped outbound/
 *    inbound strings when setting `customerWating` / `ajwWaiting` flags.
 *
 * Outbound mapping uses `RawOutboundDiscriminator` first, then falls back to
 * `RawStatus` for the *displayed row* only. When outbound is absent or generic,
 * defaults to **Request Received** (ExchangeRequestAction semantics for action
 * buckets). Status dropdown uses the static list in `exchangesStatusFilters.ts`
 */

/** Neutral labels: customer provided info without implying outcome (covered/not, in stock/not). */
export const NEUTRAL_COVERAGE_LABEL = "Coverage provided";
export const NEUTRAL_STOCK_LABEL = "Stock status provided";
export const OFF_CORE_DETAIL_LABEL = "Off Core Detail Completed";
export const UNKNOWN_STATUS_LABEL = "Unknown";

/**
 * Raw discriminators that only apply to the inbound (unserviceable) column.
 * Must not be used as outbound display when falling back from `RawStatus`.
 */
export const INBOUND_ONLY_RAW_DISCRIMINATORS = [
  "OffcoreShippingAction",
  "OffcorePartAction",
  "OffcoreDetailAction",
] as const;

/**
 * Outbound discriminators that are not a business status — display mapper may still
 * fall back to `RawStatus` for legacy rows; they are excluded from OData `$filter`.
 */
export const GENERIC_OUTBOUND_DISCRIMINATORS = [
  "CommentAction",
  "FileUploadAction",
] as const;

export type GenericOutboundDiscriminator =
  (typeof GENERIC_OUTBOUND_DISCRIMINATORS)[number];

/** Shape of a row from `GET /odata/PBHOrdersSQLOnly` before mapping. */
export interface SqlOnlyOrderRow {
  ExchangeRequestId?: number;
  Address?: string;
  AJWSO?: string;
  CompanyAutoKey?: number;
  CompanyName?: string;
  CreationDate?: string;
  CustomerPO?: string;
  DateNeeded?: string;
  PartNumber?: string;
  CustomerPriority?: string;
  Priority?: string;
  RawStatus?: string | null;
  RawOutboundDiscriminator?: string | null;
  RawInboundDiscriminator?: string | null;
  [key: string]: unknown;
}

/**
 * Legacy UI sort fields → actual `PBHOrdersSQLOnly` columns.
 *
 * The sort dropdown (`sortOptions.ts`) and detail/single-row queries use the
 * legacy `InboundStatus` / `OutboundStatus` field names, but the SQL-only OData
 * entity exposes them only as the underlying Raw* discriminators. Sending
 * `$orderby=InboundStatus` to `PBHOrdersSQLOnly` returns 400 Bad Request, so we
 * translate the field name here. Other fields (CreationDate, CustomerPO, etc.)
 * exist on both entities and pass through unchanged.
 */
const SQL_ONLY_SORT_FIELD_MAP: Record<string, string> = {
  InboundStatus: "RawInboundDiscriminator",
  OutboundStatus: "RawOutboundDiscriminator",
  Status: "RawStatus",
};

/**
 * Map a legacy sort field to its `PBHOrdersSQLOnly` column. Returns the input
 * unchanged when no translation is needed (or when falsy).
 */
export const mapSqlOnlySortField = (
  sortBy: string | null | undefined,
): string | null | undefined => {
  if (!sortBy) {
    return sortBy;
  }
  return SQL_ONLY_SORT_FIELD_MAP[sortBy] ?? sortBy;
};

/**
 * Translate a sort descriptor's `sortby` to the SQL-only column. Accepts the
 * loose `{ sortby, sortorder }` shape used across `api.service.js`.
 */
export const mapSqlOnlySort = <
  T extends { sortby?: string | null; sortorder?: string },
>(
  sort: T,
): T => {
  if (!sort || !sort.sortby) {
    return sort;
  }
  const mapped = mapSqlOnlySortField(sort.sortby);
  if (mapped === sort.sortby) {
    return sort;
  }
  return { ...sort, sortby: mapped };
};

/** API discriminator → human-readable label for inbound/outbound columns. */
const RAW_TO_DISPLAY: Record<string, string> = {
  ShipmentAction: "Shipped",
  CoveredAction: NEUTRAL_COVERAGE_LABEL,
  AcceptDeclineAction: "Accept/Decline",
  AlternativeAction: "Alternate Offered",
  ExchangeRequestAction: "Request Received",
  InStockAction: NEUTRAL_STOCK_LABEL,
  CancellationAction: "Cancelled",
  OffcoreShippingAction: "OffCore Shipping",
  OffcorePartAction: "OffCore Part Info",
  OffcoreDetailAction: OFF_CORE_DETAIL_LABEL,
  ActionDetail: "Shipped",
};

/**
 * Reverse map for `sqlOnlyFilterUtils` — turns a toolbar label back into the
 * raw discriminator used in OData `$filter` clauses.
 */
export const buildDisplayToRawDiscriminatorMap = (): Record<string, string> => {
  const map: Record<string, string> = {};
  for (const [raw, display] of Object.entries(RAW_TO_DISPLAY)) {
    if (!map[display]) {
      map[display] = raw;
    }
  }
  return map;
};

const isGenericOutbound = (
  value: string | null | undefined,
): value is GenericOutboundDiscriminator =>
  GENERIC_OUTBOUND_DISCRIMINATORS.includes(
    value as GenericOutboundDiscriminator,
  );

const INBOUND_ONLY_RAW = new Set<string>(INBOUND_ONLY_RAW_DISCRIMINATORS);

export const mapRawDiscriminatorToDisplay = (
  raw: string | null | undefined,
): string | undefined => {
  if (raw == null || raw === "") {
    return undefined;
  }
  return RAW_TO_DISPLAY[raw];
};

/**
 * Inbound column: null/empty discriminator means customer has not yet provided
 * off-core information (legacy "Awaiting OffCore Info").
 */
export const mapRawInboundDiscriminator = (
  raw: string | null | undefined,
): string => {
  if (raw == null || raw === "") {
    return "Awaiting OffCore Info";
  }
  return mapRawDiscriminatorToDisplay(raw) ?? UNKNOWN_STATUS_LABEL;
};

/**
 * Outbound column for list display: prefer outbound discriminator; when generic
 * or missing, fall back to `RawStatus` if it maps to an outbound-relevant label.
 */
export const mapRawOutboundDiscriminator = (
  rawOutbound: string | null | undefined,
  rawStatus: string | null | undefined,
): string => {
  if (rawOutbound && !isGenericOutbound(rawOutbound)) {
    const fromOutbound = mapRawDiscriminatorToDisplay(rawOutbound);
    if (fromOutbound && !INBOUND_ONLY_RAW.has(rawOutbound)) {
      return fromOutbound;
    }
  }

  if (rawStatus && !INBOUND_ONLY_RAW.has(rawStatus)) {
    const fromStatus = mapRawDiscriminatorToDisplay(rawStatus);
    if (fromStatus) {
      return fromStatus;
    }
  }

  // Unmapped / absent / generic outbound: ExchangeRequestAction semantics
  // (Request Received) for display and Waiting vs Action buckets.
  return RAW_TO_DISPLAY.ExchangeRequestAction;
};

/** Re-apply SQL-only status mapping from Raw* fields (e.g. hydrated action-scan cache). */
export const refreshSqlOnlyExchangeOrderStatus = (
  row: Record<string, unknown>,
): void => {
  if (
    row.RawOutboundDiscriminator === undefined &&
    row.RawInboundDiscriminator === undefined &&
    row.RawStatus === undefined
  ) {
    return;
  }

  const inboundStatus = mapRawInboundDiscriminator(
    row.RawInboundDiscriminator as string | null | undefined,
  );
  const outboundStatus = mapRawOutboundDiscriminator(
    row.RawOutboundDiscriminator as string | null | undefined,
    row.RawStatus as string | null | undefined,
  );

  row.InboundStatus = inboundStatus;
  row.OutboundStatus = outboundStatus;
  row.Status = outboundStatus;
};

/** Attach legacy `InboundStatus` / `OutboundStatus` fields to each SQL-only row. */
export const mapSqlOnlyOrderToExchangeOrder = (
  row: SqlOnlyOrderRow,
): Record<string, unknown> => {
  const inboundStatus = mapRawInboundDiscriminator(
    row.RawInboundDiscriminator,
  );
  const outboundStatus = mapRawOutboundDiscriminator(
    row.RawOutboundDiscriminator,
    row.RawStatus,
  );

  return {
    ...row,
    ExchangeRequestId:
      row.ExchangeRequestId != null
        ? Number(row.ExchangeRequestId)
        : row.ExchangeRequestId,
    InboundStatus: inboundStatus,
    OutboundStatus: outboundStatus,
    Status: outboundStatus,
  };
};

export const mapSqlOnlyOrders = (
  rows: SqlOnlyOrderRow[],
): Record<string, unknown>[] =>
  rows.map((row) => mapSqlOnlyOrderToExchangeOrder(row));
